import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux'
import {useTranslation} from 'react-i18next';
import {StyledInvoiceDetailsPanel} from './StyledInvoiceDetailsPanel';
import InvoiceSelector from '../InvoiceSelector';
import Spin from '../../../Spin';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {expenseTypesConstants} from '../../../../constants';
import {expensesHelpers, fileHelpers} from '../../../../utils/helpers';
import {alertActions} from '../../../../state/actions/alert.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';

const defaultAttachment = {
  contentType: undefined,
  data: undefined,
  id: undefined,
  file: undefined,
  name: undefined
}

const InvoiceDetailsPanel = ({
  allowActionsWithoutExpense = false,
  displayMissingFieldIndicator = false,
  download = true,
  edit = true,
  expense,
  onExpenseUpdate,
  onInvoiceAdd,
  onInvoiceRemove,
  ...rest
}) => {
  const [attachment, setAttachment] = useState(defaultAttachment);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [relatedInvoices, setRelatedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canStartEditingInvoice, setCanStartEditingInvoice] = useState(false);

  const [t] = useTranslation('main');

  const dispatch = useDispatch();

  const expenseId = useMemo(() => expense?.id, [expense?.id]);

  const showInvoiceAttachmentWarning = () => {
    alertActions.warning(t('validation.invoiceCannotBeAttached'), 4000, 350);
  }

  const startAttachmentLoading = () => setIsAttachmentLoading(true);

  const finishAttachmentLoading = () => setIsAttachmentLoading(false);

  const finishLoading = () => setLoading(false);

  const startLoading = () => setLoading(true);

  const showMainSpinner = useMemo(() => isAttachmentLoading || loading, [isAttachmentLoading, loading]);

  useEffect(() => {
    const enableEdit = !expensesHelpers.isDefinedExpenseType(expense);

    if (canStartEditingInvoice !== enableEdit) {
      setCanStartEditingInvoice(enableEdit);
    }
  }, [expense]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAttachment = (id) => setAttachment({...defaultAttachment, id});

  const getAttachmentDetails = (expenseId) => {
    if (!expenseId) return;

    startAttachmentLoading();
    dispatch(expenseActions.getExpenseAttachment({
      id: expenseId,
      successCallback: (data) => {
        const contentType = data['content-type']
        const file = `data:${contentType};base64,${data.file}`;
        setAttachment({
          id: expenseId,
          data,
          contentType,
          file,
          name: data.filename
        });
        finishAttachmentLoading();
      },
      errorCallback: () => {
        clearAttachment(expenseId);
        finishAttachmentLoading();
      }
    }));
  }

  const getRelatedInvoices = (expenseId) => {
    if (!expenseId) return;

    const callbackFunc = (data) => {
      setRelatedInvoices(data);
      setLoading(false);
    }

    setLoading(true);
    dispatch(expenseActions.getExpenseRelatedInvoices({
      id: expenseId,
      successCallback: callbackFunc,
      errorCallback: () => callbackFunc([])
    }));
  }

  useEffect(() => {
    if (expense) {
      const expenseId = expense?.id;
      const expenseType = expense?.expense_type;
      const isUndefinedExpenseType = expenseType === undefined || expenseType === null;

      if (isUndefinedExpenseType) {
        getRelatedInvoices(expenseId);
        if (attachment.file) {
          clearAttachment(expenseId);
        }
        return;
      }

      if (expenseId && expense.attachment_url) {
        if (expenseId === attachment.id) return;
        getAttachmentDetails(expenseId);
      } else if (attachment.file) {
        clearAttachment(expenseId);
      }
    }
  }, [expense?.attachment_url, expense?.expense_type, expense?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForm = ({data, successCallback, errorCallback} = {}) => {
    if (!expense && !allowActionsWithoutExpense) return; // Early exit if no expense.
    const { expense_type } = data;

    const isExpenseTypeUpdated = expense_type !== undefined && expense_type !== expense?.expense_type;

    const formData = isExpenseTypeUpdated ? { expense_type } : data;

    if (expense) {
      dispatch(expenseActions.updateExpense({
        id: expense.id,
        data: formData,
        successCallback: (data) => {
          onExpenseUpdate?.(data);
          successCallback?.(data);
        },
        errorCallback
      }));
    } else {
      successCallback?.(formData);
      onExpenseUpdate?.(formData);
    }
  };

  const handleRemoveInvoice = () => {
    if (onInvoiceRemove) {
      onInvoiceRemove({
        initialCallback: () => setIsAttachmentLoading(true),
        successCallback: () => {
          clearAttachment(expense?.id);
          finishAttachmentLoading();
        },
        errorCallback: finishAttachmentLoading
      });
    }
  }

  const handleLinkExpense = (invoice) => {
    const invoiceId = invoice?.id;

    if (!expenseId || !invoiceId) {
      finishLoading();
      showInvoiceAttachmentWarning();
      return;
    }

    const data = {expense_type: expenseTypesConstants[invoice?.type]};

    startLoading();

    dispatch(invoicesActions.linkExpense({
      id: invoiceId,
      data: {expense_id: expenseId},
      successCallback: () => {
        dispatch(expenseActions.updateExpense({
          id: expenseId,
          data,
          successCallback: (response) => {
            onExpenseUpdate?.(response);
            finishLoading();
          },
          errorCallback: finishLoading,
        }));
      },
      errorCallback: finishLoading
    }));
  }

  const handleInvoiceSelection = (e, invoiceId) => {
    const invoice = relatedInvoices.find(inv => inv.id === invoiceId);
    if (invoice) {
      handleLinkExpense(invoice);
    }
  };

  const handleAddInvoice = async (files) => {
    if (!files?.length) return;

    if (!expenseId && !allowActionsWithoutExpense) {
      showInvoiceAttachmentWarning();
      return;
    }

    startLoading();

    const file = files[0];
    const fileBase64 = await fileHelpers.getBase64(file.originFileObj);

    dispatch(invoicesActions.batchCreateInvoices(
      {invoices: [{base64_file: fileBase64, file_name: file.name}]},
      (invoices) => {
        if (invoices.length > 0) {
          onInvoiceAdd?.(invoices[0]);
          if (expenseId) {
            handleLinkExpense(invoices[0]);
          } else {
            setAttachment({
              contentType: file.type,
              file: fileBase64,
              name: file.name
            });
            finishLoading();
          }
        }
      },
      finishLoading
    ));
  }

  return (
    <StyledInvoiceDetailsPanel {...rest}>
      <Spin spinning={showMainSpinner} size='xsmall'>
        <InvoiceSelector
          attachment={attachment}
          expense={expense}
          isCloseDisable={!edit}
          isEditDisable={!edit}
          isDownloadDisable={!download}
          displayMissingFieldIndicator={displayMissingFieldIndicator}
          onAddInvoice={handleAddInvoice}
          onSelectInvoice={handleInvoiceSelection}
          onRemoveInvoice={handleRemoveInvoice}
          onSubmitInvoice={handleSubmitForm}
          invoices={relatedInvoices}
        />
      </Spin>
    </StyledInvoiceDetailsPanel>
  );
}

InvoiceDetailsPanel.propTypes = {
  allowActionsWithoutExpense: PropTypes.bool,
  displayMissingFieldIndicator: PropTypes.bool,
  edit: PropTypes.bool,
  expense: PropTypes.object,
  onExpenseUpdate: PropTypes.func,
  onInvoiceAdd: PropTypes.func,
  onInvoiceRemove: PropTypes.func,
}

export default InvoiceDetailsPanel;
